/**
 * ScenariosSelect component provides a workspace-dependent scenario selection dropdown
 * with GraphQL integration for data fetching.
 *
 * Key features:
 * - Dynamic scenario fetching based on selected workspace
 * - Real-time data updates with network-only fetch policy
 * - Consistent styling with workspace selector
 * - Loading state handling with skeleton loader
 * - Error state fallback
 */

import React, { useMemo } from "react";
import cx from "classnames";
import { useQuery } from "@apollo/client";
import { Pill, Select, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import scenariosBaseQuery3 from "../../graphql/scenariosBase.query.graphql";
import styles from "./scenariosSelect.module.scss";
import { Icon } from "@foris/avocado-icons";

/**
 * Props interface for ScenariosSelect component
 * @property {any} workspaceValue - Selected workspace to filter scenarios
 * @property {any} scenarioValue - Currently selected scenario
 * @property {function} onChange - Callback function triggered when selection changes
 */
interface SelectProps {
  workspaceValue?: any;
  scenarioValue?: any;
  showAdvancedOptions?: boolean;
  showOnlyProductive?: boolean;
  isClearable?: boolean;
  onChange: (value: any) => void;
}

const ScenariosSelect = ({
  workspaceValue,
  onChange,
  scenarioValue,
  showAdvancedOptions = true,
  showOnlyProductive,
  isClearable,
}: SelectProps) => {
  const { t } = useTranslation();

  // Fetch scenarios filtered by workspace, ensuring fresh data with network-only policy
  const { loading, error, data } = useQuery<any, any>(scenariosBaseQuery3, {
    variables: {
      scenarioFilter:
        workspaceValue?.value || showOnlyProductive
          ? {
              workspaceId: workspaceValue?.value ? workspaceValue?.value : undefined,
              status: showOnlyProductive ? "PRODUCTIVE" : undefined,
            }
          : undefined,
    },
    fetchPolicy: "network-only",
  });

  // Transform scenario data into select options format
  const options = useMemo(
    () =>
      error || loading
        ? []
        : (data?.base?.scenarios ?? []).map((value: any) => {
            return { label: value.name, value: value.id, self: value };
          }),
    [data, error, loading],
  );

  return (
    <div className={styles.scenariosSelectContainer}>
      <Select
        className={cx(
          styles.scenariosSelect,
          showAdvancedOptions && styles.scenariosSelect__isAdvanced,
        )}
        label={t("context-modal.scenario")}
        maxMenuHeight={150}
        options={options}
        isLoading={loading}
        placeholder={t("context-modal.select-scenario")}
        value={scenarioValue}
        formatOptionLabel={
          showAdvancedOptions
            ? (option: any) => (
                <div className={styles.scenariosSelect_option}>
                  <div className={styles.optionDetails}>
                    <Text className={styles.optionDetails_title} type="sm">
                      {option?.self?.id ?? option?.value ?? "-"}{" "}
                      <strong className={styles.titleLabel}>
                        {option?.self?.name ?? option?.label ?? ""}
                      </strong>
                    </Text>

                    <Text className={styles.optionDetails_description} type="xs">
                      {t("context-modal.details.workspace-short")}:{" "}
                      <span className={styles.descriptionLabel}>
                        {option?.self?.workspace?.name ?? workspaceValue?.label ?? "-"}
                      </span>
                    </Text>
                  </div>

                  {option?.self?.status?.value === "PRODUCTIVE" && (
                    <Pill
                      className={styles.optionStatus}
                      icon="star"
                      iconFilled
                      iconColor="neutral-50"
                    >
                      {t("context-modal.details.productive")}
                    </Pill>
                  )}
                </div>
              )
            : undefined
        }
        onChange={(value: any): void => onChange(value)}
      />

      {!loading && !!scenarioValue && isClearable && (
        <button className={styles.scenariosSelectContainer_clearBtn} onClick={() => onChange(null)}>
          <Icon name="close" size="sm" color="neutral-50" />
        </button>
      )}
    </div>
  );
};

export default ScenariosSelect;
